import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import Slider from 'react-input-slider'
import ResizeObserver from 'rc-resize-observer'
import classNames from 'classnames'
import { useSelector, useDispatch } from 'react-redux'
import shortid from 'shortid'
import numeral from 'numeraljs'
import { History } from 'history'
import moment from 'moment'

import styles from '../component-styles/campaign-codes-table.module.scss'
import stylesLabel from '../component-styles/select-box-typeahead.module.scss'
// import TopScrollbar from './top-scrollbar'
import useMobile from '../hooks/useMobile'
import useResize from '../hooks/useResize'
import {
  getCampaignCodeGeneratorAsync,
  updateCampaignCodeAsync,
  getBatchQueryAsync,
  getStoredCodeStatsAsync,
  getValidateResultsAsync,
  getStoredCodeStatsGQL,
} from '../store/actions'
import { CampaignCodesState } from '../store/campaign-codes-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import {
  GeneratorFields,
  CampaignCodesCompressed,
  CampaignCodeGeneratorStructure,
} from '../api-client/api-types'
import { clientInstance } from '../store/sagas'
import Input, { SearchInput } from './input'
import { getItemByKeyValue } from '../helpers'
import OrderArrow from './order-arrow'
import Button, { ButtonRow } from './button'
import HeaderPanel, { Panel } from './header-panel'
import { codeStatus } from '../core/variables'
import Pagination from './pagination'
import {
  getAdobeCodesDownloadLink,
  downloadSpecificCodes,
} from '../api-client/track-client'
import { SelectBoxSimple, SelectBoxButton, MultiSelectBox } from './select-box'
import { SiteState } from '../store/site-reducer'
import BulkImport from './bulk-import'
import CreateQrModal from './create-qr-modal'
import BulkDeleteModal from './bulk-delete-modal'
import CampaignCode from './campaign-code'
import {
  getCsvString,
  getAnchorFromString,
  getMetricStatus,
} from '../helpers/campaign-codes'
import ToolTip from './tooltip'
import ShareCampaignCodesButton from './share-campaign-codes-button'
import { saveFormData } from '../local-client/local-client'
import useTableSortFilter, {
  SearchFn,
  SearchFns,
  SortFn,
  SortFns,
} from '../hooks/useTableSortFilter'
import { Preloader } from './loader'

// moment.updateLocale('en', {
//   relativeTime: {
//     future: 'in %s',
//     past: '%s ago',
//     s: 'seconds',
//     ss: '%ss',
//     m: 'a minute',
//     mm: '%dm',
//     h: 'an hour',
//     hh: '%dh',
//     d: 'a day',
//     dd: '%dd',
//     M: 'a month',
//     MM: '%dM',
//     y: 'a year',
//     yy: '%dY',
//   },
// })

interface HeaderColumnsProps {
  allChecked: any
  setAllChecked: any
  setSelectedCodes: any
  orderedData: any
  setSortOrder: any
  sortKey: any
  orderAsc: any
  selectedMetrics: any
  selectedMetricsIndexes: number[]
  metrics: any
  generatedStructure: CampaignCodeGeneratorStructure
  fwdRef?: any
  headerWidths?: any
}

export function HeaderColumns({
  allChecked,
  setAllChecked,
  setSelectedCodes,
  orderedData,
  setSortOrder,
  sortKey,
  orderAsc,
  selectedMetrics,
  selectedMetricsIndexes,
  metrics,
  generatedStructure,
  fwdRef = null,
  headerWidths = [],
}: HeaderColumnsProps): React.ReactElement {
  let columnIndex = 4

  return (
    <tr ref={fwdRef}>
      <th
        className={styles.checkboxCell}
        style={headerWidths[0] ? headerWidths[0] : {}}
      >
        <Input
          type="checkbox"
          id="allNone"
          name="allNone"
          checked={allChecked}
          className={styles.checkboxItem}
          label=" "
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            const { checked } = e.target as HTMLInputElement
            setAllChecked(checked)
            if (!checked) {
              setSelectedCodes([])
            } else {
              const codesToSet: string[] = []
              // select all codes
              orderedData.forEach((page: CampaignCodesCompressed[]) => {
                page.forEach((item: CampaignCodesCompressed) => {
                  const { cID = '' } = item
                  codesToSet.push(cID)
                })
              })
              setSelectedCodes(codesToSet)
            }
          }}
        />
      </th>
      <th
        className={styles.dateCell}
        style={headerWidths[1] ? headerWidths[1] : {}}
      >
        <Button
          className={styles.headerButton}
          onClick={(): void => {
            setSortOrder('t')
          }}
          type="none"
        >
          Created
          <OrderArrow
            className={styles.orderArrow}
            currentKey="t"
            sortKey={sortKey}
            orderAsc={orderAsc}
          />
        </Button>
      </th>
      <th
        className={classNames(styles.statusCell, styles.shortLinkCell)}
        style={headerWidths[2] ? headerWidths[2] : {}}
      >
        <Button
          className={styles.headerButton}
          onClick={(): void => {
            setSortOrder('sL')
          }}
          type="none"
        >
          Short link
          <OrderArrow
            className={styles.orderArrow}
            currentKey="sL"
            sortKey={sortKey}
            orderAsc={orderAsc}
          />
          <ToolTip
            showUnder
            className={classNames(styles.tooltip, styles.headerTooltip)}
            message={`
A short, memorable link that redirects to the landing page with campaign code, capturing valuable clickthrough data. 

When available, use this link rather than the landing page with campaign code link.`}
          />
        </Button>
      </th>
      <th
        className={styles.codeColumn}
        style={headerWidths[3] ? headerWidths[3] : {}}
      >
        <Button
          className={styles.headerButton}
          onClick={(): void => {
            setSortOrder('c')
          }}
          type="none"
        >
          Landing page with campaign code
          <OrderArrow
            className={styles.orderArrow}
            currentKey="c"
            sortKey={sortKey}
            orderAsc={orderAsc}
          />
          <ToolTip
            showUnder
            className={classNames(styles.tooltip, styles.headerTooltip)}
            message="The landing page [validation status](https://support.uplifter.ai/hc/en-us/articles/360020525658-What-does-landing-page-validation-do-)."
          />
        </Button>
      </th>
      <th
        className={styles.statusCell}
        style={headerWidths[4] ? headerWidths[4] : {}}
      >
        <Button
          className={styles.headerButton}
          onClick={(): void => {
            setSortOrder('codeType')
          }}
          type="none"
        >
          Status
          <OrderArrow
            className={styles.orderArrow}
            currentKey="codeType"
            sortKey={sortKey}
            type="bidirectional"
          />
        </Button>
      </th>
      {selectedMetrics.map((item, index) => {
        const useMetric = getItemByKeyValue(metrics, 'metricID', item)
        columnIndex += 1
        if (useMetric === -1) {
          return (
            <th
              style={headerWidths[columnIndex] ? headerWidths[columnIndex] : {}}
              className={styles.otherColumn}
              key={shortid.generate()}
            />
          )
        }

        return (
          <th
            className={styles.otherColumn}
            key={shortid.generate()}
            style={headerWidths[columnIndex] ? headerWidths[columnIndex] : {}}
          >
            <Button
              className={styles.headerButton}
              onClick={(): void => {
                setSortOrder(`metric:${selectedMetricsIndexes[index]}`, false)
              }}
              type="none"
            >
              {useMetric.displayName}

              <ToolTip
                showUnder
                className={classNames(styles.tooltip, styles.headerTooltip)}
                message={useMetric.helpText}
              />

              <OrderArrow
                className={styles.orderArrow}
                currentKey={`metric:${selectedMetricsIndexes[index]}`}
                sortKey={sortKey}
                orderAsc={orderAsc}
              />
            </Button>
          </th>
        )
      })}
      {generatedStructure.paramDefs.map(
        (field: GeneratorFields, index: number): React.ReactElement => {
          columnIndex += 1
          return (
            <th
              key={shortid.generate()}
              className={styles.otherColumn}
              style={headerWidths[columnIndex] ? headerWidths[columnIndex] : {}}
            >
              <Button
                className={styles.headerButton}
                onClick={(): void => {
                  setSortOrder(`${index}-${field.fieldName}`)
                }}
                type="none"
              >
                {field.fieldName}
                <OrderArrow
                  className={styles.orderArrow}
                  currentKey={`${index}-${field.fieldName}`}
                  sortKey={sortKey}
                  orderAsc={orderAsc}
                />
                <ToolTip
                  showUnder
                  showOnLeft
                  className={classNames(styles.tooltip, styles.headerTooltip)}
                  message={field.helpText}
                />
              </Button>
            </th>
          )
        },
      )}
    </tr>
  )
}

export function getWidths(headerRef) {
  const result: any[] = []
  if (headerRef && headerRef.current) {
    const allTh = headerRef.current.querySelectorAll('th')
    allTh.forEach((th) => {
      const dims = th.getBoundingClientRect()
      const { width } = dims
      result.push({
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        width: `${width}px`,
      })
    })
  }
  return result
}

export const cloneAndEdit = (
  codes,
  selectedCodes,
  accountID: string,
  history,
  masterPrefix = '?',
  paramDefs,
) => {
  const result: { url: string[]; data: any } = {
    url: [],
    data: {},
  }
  selectedCodes.forEach((cID) => {
    const found = getItemByKeyValue(codes, 'cID', cID)
    if (found !== -1) {
      const { cDef, c } = found
      const useAnchor = getAnchorFromString(c, masterPrefix)
      cDef.forEach((item) => {
        const { fID, n } = item
        const s = c.split(masterPrefix)
        const useUrl = `${s.length > 0 ? s[0] : ''}${useAnchor}`
        if (result.url.indexOf(useUrl) === -1) {
          result.url = [...result.url, useUrl]
        }
        const foundParamDef = getItemByKeyValue(paramDefs, 'fieldID', fID)
        let valueExists = false
        let useValue = n
        if (foundParamDef !== -1) {
          valueExists = true
          const { selectFields } = foundParamDef
          if (selectFields) {
            const selectFieldValueFound = getItemByKeyValue(
              selectFields,
              'optionName',
              useValue,
            )
            valueExists = selectFieldValueFound !== -1
            if (valueExists) {
              useValue = selectFieldValueFound.optionValue
            }
          }
        }

        const isNull = useValue === null
        const isEmpty = useValue === ''
        if (valueExists && !isNull && !isEmpty) {
          if (
            Object.prototype.hasOwnProperty.call(result.data, fID) &&
            Object.prototype.hasOwnProperty.call(
              result.data[fID],
              'optionValue',
            )
          ) {
            if (result.data[fID].optionValue.indexOf(useValue) === -1) {
              let useOptionValue = [...result.data[fID].optionValue, useValue]
              if (foundParamDef && foundParamDef.metaParameter) {
                useOptionValue = [useOptionValue.join(',')]
              }
              result.data = {
                ...result.data,
                [fID]: {
                  optionValue: useOptionValue,
                },
              }
            }
          } else {
            result.data = {
              ...result.data,
              [fID]: { optionValue: [useValue] },
            }
          }
        }
      })
    }
  })
  const single = selectedCodes.length === 1

  saveFormData(result.url, result.data, accountID, single)
  history.push('/track/create-campaign-codes')
}
interface Props {
  history: History
}

export default function CampaignCodesTable({
  history,
}: Props): React.ReactElement | null {
  const mobile = useMobile(769)
  const isMobileMenu = useMobile(1200)

  const dispatch = useDispatch()
  const {
    campaignCodes,
    graphql,
    site,
  }: {
    campaignCodes: CampaignCodesState
    graphql: GraphQLState
    site: SiteState
  } = useSelector((s: any): any => s)

  const {
    codes,
    loadingStatus,
    generatedStructure,
    batchCodes,
    validatedUrls,
  } = campaignCodes || {}
  const { currentAccount, currentUser } = graphql
  const currentAccountPermission = currentUser?.currentAccountPermission || ''
  const email = currentUser?.email || ''

  const isAdminUser =
    currentAccountPermission === 'admin' ||
    currentAccountPermission === 'support'

  const accountID = currentAccount?.accountID || ''

  const { storedCodeStats, loading: graphqlLoading } = graphql

  const loadingStatsStatus = useMemo(() => {
    return graphqlLoading.indexOf('campaignLinkDashboardMetricData') !== -1
  }, [graphqlLoading])

  const { masterPrefix, paramDefs } = generatedStructure || {
    masterPrefix: '?',
    paramDefs: [],
  }
  const [sliderState, setSliderState] = useState({ x: 0, y: 0 })
  const [selectedCodes, setSelectedCodes] = useState<string[]>([])
  const [uploadState, setUploadState] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showQrModal, setShowQrModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const screenWidth = useResize()
  const isWideScreen = screenWidth >= 1200
  const [allChecked, setAllChecked] = useState(false)
  const [headerWidths, setHeaderWidths] = useState<any[]>([])

  const headerRef = useRef<HTMLTableElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const mainTableColumnRef = useRef<HTMLTableElement>(null)
  const fixedHeaderRef = useRef<HTMLTableElement>(null)
  const [metrics, setMetrics] = useState<MetricSource[]>([])
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([])
  const [loaded, setLoaded] = useState(false)

  const [indexedKeys, setIndexedKeys] = useState({})
  const [codeTypeSort, setCodeTypeSort] = useState<
    SortFn<CampaignCodesCompressed>
  >(() => '')

  useEffect(() => {
    const keys = Object.keys(codeStatus)
    const makeIndexedKeys = {}
    keys.forEach((item, index) => {
      if (item !== 'na') {
        makeIndexedKeys[codeStatus[item]] = index
      }
    })
    setIndexedKeys(makeIndexedKeys)
  }, [])

  const metricSorts = useMemo(() => {
    const allMetrics: SortFns<CampaignCodesCompressed> = {}

    if (storedCodeStats) {
      const { codeIDs, metricValues } = storedCodeStats

      Object.keys(metricValues).forEach((metricKey, useMetric) => {
        const { mf365 } = metricValues[metricKey]
        allMetrics[`metric:${metricKey}`] = (row, currentOrderAsc) => {
          const { cID } = row
          const found = cID ? codeIDs.indexOf(cID) : -1
          if (found !== -1) {
            return mf365[found]
          }
          return currentOrderAsc ? Infinity : -1
        }
      })
    }

    return allMetrics
  }, [storedCodeStats, codes])

  const fieldSorts = useMemo(() => {
    const allFields: SortFns<CampaignCodesCompressed> = {}

    if (storedCodeStats && paramDefs) {
      paramDefs.forEach(({ fieldName }, index) => {
        allFields[`${index}-${fieldName}`] = (row) => {
          return row.cDef[index].n || ''
        }
      })
    }

    return allFields
  }, [paramDefs, codes])

  const fieldSearches = useMemo(() => {
    const allFields: SearchFns<CampaignCodesCompressed[]> = {}

    if (storedCodeStats && paramDefs) {
      paramDefs.forEach(({ fieldID }, index) => {
        if (fieldID) {
          allFields[fieldID] = (data, searchTerm = '') => {
            const searchPattern = searchTerm
              .replace(/[^a-zA-Z0-9]/g, '')
              .split('')
              .join('.*')

            return data.concat().filter((code) => {
              return (
                code.cDef[index].n
                  .toLowerCase()
                  .match(new RegExp(searchPattern, 'i')) !== null
              )
            })
          }
        }
      })
    }
    return allFields
  }, [codes, storedCodeStats])

  const sLSearch: SearchFn<CampaignCodesCompressed[]> = useCallback(
    (data, searchTerm = '') => {
      const useSearchTerm = searchTerm.replace('https://upl.inc/', '')
      const searchPattern = useSearchTerm.toLowerCase()

      return data.concat().filter((code) => {
        return (
          code.sL.toLowerCase().match(new RegExp(searchPattern, 'i')) !== null
        )
      })
    },
    [codes, storedCodeStats],
  )

  const createdSearch: SearchFn<CampaignCodesCompressed[]> = useCallback(
    (data, searchTerm = '') => {
      return data.concat().filter((code) => {
        const { t } = code
        const dateProp = t
        const addToSearch = moment(dateProp).format(
          'DD-MM-YYYY, DD/MM/YYYY, MMMM YYYY, MMM YYYY',
        )
        return (
          addToSearch.toLowerCase().match(new RegExp(searchTerm, 'i')) !== null
        )
      })
    },
    [codes, storedCodeStats],
  )

  const codeTypeSearch: SearchFn<CampaignCodesCompressed[]> = useCallback(
    (data, searchTerm = '') => {
      const searchPattern = searchTerm
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .split('')
        .join('.*')
      const { status, codeIDs } = storedCodeStats
      return data.filter((code) => {
        const codeIdIndex = codeIDs.indexOf(code.cID)
        const codeType = getMetricStatus(code.t, status, codeIdIndex)

        return (
          codeType.toLowerCase().match(new RegExp(searchPattern, 'i')) !== null
        )
      })
    },
    [codes, storedCodeStats],
  )

  const allSearch: SearchFn<CampaignCodesCompressed[]> = useCallback(
    (data, searchTerm = '') => {
      const useSearchTerm = searchTerm.replace('https://upl.inc/', '')
      const searchPattern = useSearchTerm.toLowerCase()

      const { status, codeIDs } = storedCodeStats

      return data.concat().filter((code) => {
        const { cID, t } = code
        const dateProp = t
        const addToSearch = moment(dateProp).format(
          'DD-MM-YYYY, DD/MM/YYYY, MMMM YYYY, MMM YYYY',
        )
        const codeIdIndex = codeIDs.indexOf(cID)
        const codeType = getMetricStatus(code.t, status, codeIdIndex)

        const searchableFields = {
          c: code.c,
          e: code.e,
          sL: code.sL,
          t: addToSearch,
          codeType,
        }

        if (storedCodeStats && paramDefs) {
          paramDefs.forEach(({ fieldName, fieldID }, index) => {
            const { cDef } = code
            const value = cDef.find(({ fID }) => fID === fieldID)?.n
            searchableFields[`${index}-${fieldName}`] = value || ''
          })
        }

        return (
          JSON.stringify(Object.values(searchableFields))
            .replace(/[\[\],\"]/g, '')
            .toLowerCase()
            .match(new RegExp(searchPattern, 'i')) !== null
        )
      })
    },
    [codes, storedCodeStats],
  )

  const {
    orderAsc,
    sortKey,
    setSortOrder,
    rowsPerPage,
    setRowsPerPage,
    pages,
    activePage,
    setActivePage,
    setSelectedSearchType,
    searchTerm,
    setSearchTerm,
    count,
    orderedList: orderedData,
  } = useTableSortFilter(
    codes,
    't',
    '',
    25,
    {
      codeType: codeTypeSearch,
      all: allSearch,
      sL: sLSearch,
      created: createdSearch,
      ...fieldSearches,
    },
    {
      codeType: codeTypeSort,
      ...metricSorts,
      ...fieldSorts,
    },
    false,
  )

  useEffect(() => {
    // Updates the 'codeType' sort function
    if (
      sortKey === 'codeType' &&
      indexedKeys &&
      storedCodeStats &&
      orderedData.length > 0
    ) {
      // Code type is the only one whose sort function will change. The others have their own effect with fewer dependencies
      const keys = Object.keys(indexedKeys)
      const last = keys.shift() as 'string'
      keys.push(last)
      const useIndexedKeys = {}
      keys.forEach((item, index) => {
        useIndexedKeys[item] = index
      })
      setIndexedKeys(useIndexedKeys)
      const { status, codeIDs } = storedCodeStats

      setCodeTypeSort(() => {
        return (row) => {
          const { cID } = row

          const found = codeIDs.indexOf(cID)
          const stat = found !== -1 ? status[found] : 'Unused'
          return useIndexedKeys[stat]
        }
      })
    }
  }, [orderAsc, sortKey, storedCodeStats, orderedData])

  useEffect((): void => {
    if (storedCodeStats) {
      if (storedCodeStats?.metricValues) {
        setMetrics(storedCodeStats?.metricValues || [])
      }
    }
  }, [storedCodeStats])

  const showFixedFooter = !loadingStatus && rowsPerPage > 10

  const showSlider = useMemo(() => {
    if (
      showFixedFooter &&
      containerRef &&
      headerRef &&
      containerRef.current &&
      headerRef.current
    ) {
      const scrollDiff =
        headerRef.current.clientWidth - containerRef.current.clientWidth
      return !!(scrollDiff > 0)
    }
    return false
  }, [
    showFixedFooter,
    containerRef?.current?.clientWidth,
    headerRef?.current?.clientWidth,
  ])

  let padding = 0
  useEffect(() => {
    const onScroll = (e) => {
      window.requestAnimationFrame(() => {
        if (mainTableColumnRef && mainTableColumnRef.current) {
          const mainRect = mainTableColumnRef.current.getBoundingClientRect()
          const topLimit = isMobileMenu ? 60 : 0
          if (mainRect.top < topLimit) {
            // setPadding(topLimit - mainRect.top)
            padding = topLimit - mainRect.top
          } else {
            padding = 0
          }
        }
        if (fixedHeaderRef && fixedHeaderRef.current) {
          fixedHeaderRef.current.style.marginTop = `${padding}px`
        }
      })
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [isMobileMenu])

  const isAdobe = !!(
    site &&
    site.selected &&
    site.selected.dataSource === 'AA_REPORT'
  )

  useEffect((): void => {
    dispatch(getCampaignCodeGeneratorAsync())
    dispatch(
      getValidateResultsAsync({
        startDate: 'last-30-days',
      }),
    )
    dispatch(getStoredCodeStatsAsync())
    dispatch(getStoredCodeStatsGQL())
  }, [])

  useEffect((): void => {
    if (site && site.selected && !loaded) {
      setSelectedMetrics([])
      // setHeaderWidths([])
      setLoaded(true)
      // dispatch(getCampaignCodesByAccountAsync())
    }
  }, [site, loaded])

  useEffect((): void => {
    if (batchCodes.length > 0 && codes.length === 0) {
      batchCodes.forEach((batchCodeArr) => {
        dispatch(
          getBatchQueryAsync({
            batch: batchCodeArr,
          }),
        )
      })
    }
    // eslint-disable-next-line
  }, [batchCodes])

  const updateParam = async (form: CampaignCodesCompressed): Promise<void> => {
    const { cID, c, cDef } = form
    const data = {
      codeID: cID,
      fullCode: c,
      paramDefs: cDef.map((item) => {
        return {
          fieldID: item.fID,
          optionName: item.n,
          // optionValue: item.v,
        }
      }),
    }
    dispatch(
      updateCampaignCodeAsync({
        compressed: form,
        full: data,
      }),
    )
  }

  const removeCodeById = (id: string) => {
    const indexFound = selectedCodes.indexOf(id)
    if (indexFound !== -1) {
      const sc = [...selectedCodes]
      sc.splice(indexFound, 1)
      setSelectedCodes(sc)
    }
  }

  const selectedMetricsIndexes = useMemo(() => {
    const result: number[] = []
    selectedMetrics.forEach((m) => {
      const index = getItemByKeyValue(metrics, 'metricID', m, true)
      result.push(index)
    })

    return result
  }, [selectedMetrics, metrics])

  const cellFieldIds = useMemo(() => {
    if (generatedStructure) {
      return generatedStructure.paramDefs.map(
        (item: GeneratorFields) => item.fieldID || '',
      )
    }
    return []
  }, [generatedStructure])

  const searchTypeList = useMemo(() => {
    const result = [{ name: 'All', value: 'all' }]
    if (generatedStructure) {
      const fields = generatedStructure.paramDefs.map(
        (item: GeneratorFields) => {
          return {
            name: item.fieldName || '',
            value: item.fieldID || '',
          }
        },
      )
      const allTypes = [
        ...result,
        {
          name: 'Created',
          value: 'created',
        },
        {
          name: 'Created by',
          value: 'e',
        },
        {
          name: 'Short link',
          value: 'sL',
        },
        ...fields,
      ]

      if (storedCodeStats) {
        allTypes.push({
          name: 'Status',
          value: 'codeType',
        })
      }
      return allTypes
    }
    return result
  }, [generatedStructure])

  const selectedCodesCanDelete = useMemo(() => {
    if (isAdminUser) {
      return [...selectedCodes]
    }
    if (!email || codes.length === 0 || selectedCodes.length === 0) {
      return []
    }
    return selectedCodes.filter((item) => {
      const found = getItemByKeyValue(codes, 'cID', item)
      return found && found.e === email
    })
  }, [isAdminUser, selectedCodes, email])

  const triggerUploadToAdobe = async (): Promise<void> => {
    setUploadState('uploading')
    const res = await clientInstance.generateSaintFile()
    if (res) {
      setUploadState('completed')
    } else {
      setUploadState('error')
    }
  }

  const adobeMessages = () => {
    if (!isAdobe) {
      return <></>
    }
    if (uploadState === 'uploading') {
      return <p className={styles.statusMessage}>Generating file...</p>
    }

    return (
      <>
        {uploadState === 'completed' && (
          <p className={styles.statusMessage}>Uploaded successfully...</p>
        )}
        {uploadState === 'error' && (
          <p className={styles.statusMessageError}>File upload failed...</p>
        )}
      </>
    )
  }

  const adobeButtons = useCallback(() => {
    if (!isAdobe) {
      return <></>
    }

    const disabled = uploadState === 'uploading'
    return (
      <>
        <span className={stylesLabel.itemLabel}>
          <span>
            <img
              src="/aa-logo.svg"
              className={stylesLabel.labelIcon}
              alt="AA"
            />
            <>Adobe Analytics</>
          </span>
        </span>
        <Button
          disabled={disabled}
          type="selectBox"
          onClick={() => {
            triggerUploadToAdobe()
          }}
        >
          Export to Adobe
        </Button>
        <Button type="selectBox" href={getAdobeCodesDownloadLink()} download>
          Download Adobe file
        </Button>
      </>
    )
  }, [isAdobe, uploadState])

  if (generatedStructure === null) {
    return null
  }

  const selectedAllCodes =
    selectedCodes.length > 0 && selectedCodes.length === codes.length

  const wrapperStyles = classNames({
    [styles.showSlider]: showSlider,
    [styles.showFixedFooter]: showFixedFooter,
  })

  return (
    <div className={wrapperStyles} ref={wrapperRef}>
      <HeaderPanel>
        <Panel>
          <ButtonRow className={styles.panelButtonRow}>
            <SearchInput
              className={styles.searchInput}
              onChange={(value) => {
                setSearchTerm(value)
                setActivePage(1)
              }}
              searchTypeList={searchTypeList}
              onChangeSearchType={(type: string) => {
                setSelectedSearchType(type)
              }}
              delay={250}
              loading={loadingStatus}
              loadingLabel="Counting links"
            >
              <>
                {searchTerm !== '' && count !== codes.length
                  ? `${numeral(count).format('0,0')}/`
                  : ''}
                {numeral(codes.length).format('0,0')} links
              </>
            </SearchInput>
          </ButtonRow>
        </Panel>
        <Panel>
          <ButtonRow className={styles.panelButtonRow}>
            {adobeMessages()}
            <MultiSelectBox
              id="select-key-metrics"
              className={styles.multiSelect}
              value={selectedMetrics}
              items={metrics}
              placeholder="Metrics (last 365 days)"
              keyName="displayName"
              keyValue="metricID"
              onChange={(val) => {
                setSelectedMetrics(val)
              }}
            />
            <SelectBoxButton
              value="Actions"
              className={styles.bulkButtons}
              action
            >
              <>
                <ShareCampaignCodesButton
                  selectedCodes={selectedCodes}
                  showSummaryTable={false}
                />
                <Button
                  disabled={loadingStatsStatus || codes.length === 0}
                  type="selectBox"
                  onClick={async () => {
                    const csv = getCsvString({
                      metrics,
                      selectedMetrics,
                      selectedMetricsIndexes,
                      storedCodeStats,
                      generatedStructure,
                      selectedCodes,
                      codes,
                    })
                    await downloadSpecificCodes(csv)
                  }}
                >
                  {selectedCodes.length === 0 || selectedAllCodes ? (
                    <>Download all </>
                  ) : (
                    <>
                      Download selected (
                      {numeral(selectedCodes.length).format('0,0')})
                    </>
                  )}
                </Button>
                <Button
                  type="selectBox"
                  disabled={selectedCodes.length === 0}
                  onClick={() => {
                    cloneAndEdit(
                      codes,
                      selectedCodes,
                      accountID,
                      history,
                      masterPrefix,
                      paramDefs,
                    )
                  }}
                >
                  Clone and edit
                </Button>
                {isAdminUser && (
                  <Button
                    type="selectBox"
                    onClick={() => {
                      setShowModal(true)
                    }}
                    download
                  >
                    Import from CSV
                  </Button>
                )}
                <Button
                  disabled={selectedCodes.length !== 1}
                  type="selectBox"
                  onClick={() => {
                    setShowQrModal(true)
                  }}
                  download
                >
                  Download QR code
                </Button>
                {adobeButtons()}
                <Button
                  type="selectBox"
                  disabled={selectedCodes.length === 0}
                  onClick={() => {
                    setShowDeleteModal(true)
                  }}
                >
                  Delete {selectedAllCodes ? 'all' : 'selected'}{' '}
                  {selectedCodes.length > 0 &&
                    `(${numeral(selectedCodes.length).format('0,0')})`}
                </Button>
              </>
            </SelectBoxButton>
          </ButtonRow>
        </Panel>
      </HeaderPanel>
      <div
        ref={containerRef}
        className={styles.container}
        // style={{
        //   maxHeight: `${containerHeight > 0 ? containerHeight : 2000}px`,
        // }}
      >
        {loadingStatus ? (
          <Preloader className={styles.loading} />
        ) : (
          <ResizeObserver
            onResize={() => {
              setHeaderWidths(getWidths(headerRef))
              // setContainerHeight(getHeigth(footerRef, introRef, screenHeight))
            }}
          >
            <table
              className={classNames(styles.table, {
                [styles.fixedHeaders]: headerWidths.length > 999,
              })}
              ref={mainTableColumnRef}
            >
              <thead>
                <HeaderColumns
                  fwdRef={headerRef}
                  allChecked={allChecked}
                  setAllChecked={setAllChecked}
                  setSelectedCodes={setSelectedCodes}
                  orderedData={orderedData}
                  setSortOrder={setSortOrder}
                  sortKey={sortKey}
                  orderAsc={orderAsc}
                  selectedMetrics={selectedMetrics}
                  selectedMetricsIndexes={selectedMetricsIndexes}
                  metrics={metrics}
                  generatedStructure={generatedStructure}
                />
              </thead>
              <tbody>
                {orderedData &&
                  orderedData[activePage - 1] &&
                  orderedData[activePage - 1].map(
                    (
                      code: CampaignCodesCompressed,
                      index: number,
                    ): React.ReactElement => {
                      const { cID, e } = code
                      const canEdit =
                        isAdminUser || email.toLowerCase() === e.toLowerCase()
                      return (
                        <CampaignCode
                          canEdit={canEdit}
                          validatedUrl={
                            cID &&
                            Object.prototype.hasOwnProperty.call(
                              validatedUrls,
                              cID,
                            )
                              ? validatedUrls[cID]
                              : null
                          }
                          storedCodeStats={storedCodeStats}
                          rowIndex={index}
                          generatedStructure={generatedStructure}
                          isAdobe={isAdobe}
                          cellsOrder={cellFieldIds}
                          isWideScreen={isWideScreen}
                          key={cID}
                          code={code}
                          updateParam={updateParam}
                          selectedCodes={selectedCodes}
                          loading={loadingStatsStatus}
                          selectedMetrics={selectedMetrics}
                          selectedMetricsIndexes={selectedMetricsIndexes}
                          lastRow={
                            orderedData[activePage - 1].length === index + 1
                          }
                          setSelectedCodes={(_cID, checked) => {
                            const hasCode = !!(
                              _cID && selectedCodes.indexOf(_cID) !== -1
                            )
                            if (hasCode) {
                              if (!checked) {
                                removeCodeById(_cID)
                              }
                              return
                            }
                            if (checked) {
                              setSelectedCodes([...selectedCodes, _cID])
                            }
                          }}
                        />
                      )
                    },
                  )}
                {!loadingStatus && orderedData && orderedData.length === 0 && (
                  <tr>
                    <td colSpan={100} className={styles.emptyDataCell}>
                      <b />
                      <span>We have no data for your search term.</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </ResizeObserver>
        )}
        {!loadingStatus && (
          <table
            className={classNames(styles.table, styles.fixedHeaderTable, {
              [styles.fixedHeaders]: headerWidths.length > 0,
              [styles.isFixed]: !mobile && padding > 0,
            })}
            ref={fixedHeaderRef}
          >
            <thead>
              <HeaderColumns
                headerWidths={headerWidths}
                allChecked={allChecked}
                setAllChecked={setAllChecked}
                setSelectedCodes={setSelectedCodes}
                orderedData={orderedData}
                setSortOrder={setSortOrder}
                sortKey={sortKey}
                orderAsc={orderAsc}
                selectedMetrics={selectedMetrics}
                selectedMetricsIndexes={selectedMetricsIndexes}
                metrics={metrics}
                generatedStructure={generatedStructure}
              />
            </thead>
          </table>
        )}
      </div>
      <ButtonRow className={styles.paginationRow}>
        {showSlider && (
          <div className={styles.sliderWrapper}>
            <Slider
              styles={{
                track: {
                  backgroundColor: '#edf2f7',
                  height: 20,
                  border: '1px solid #a0aec0',
                },
                active: {
                  backgroundColor: '#e2e8f0',
                },
                thumb: {
                  backgroundColor: '#cbd5e0',
                  border: '1px solid #a0aec0',
                  borderRadius: 4,
                  width: 40,
                  height: 18,
                },
                disabled: {
                  opacity: 0.5,
                },
              }}
              xstep={1}
              axis="x"
              x={sliderState.x}
              onChange={(dims: { x: number; y: number }) => {
                setSliderState(dims)
                if (containerRef && containerRef.current) {
                  if (mainTableColumnRef && mainTableColumnRef.current) {
                    const container = containerRef.current.getBoundingClientRect()
                    const table = mainTableColumnRef.current.getBoundingClientRect()
                    const { width } = table
                    const { width: containerWidth } = container
                    const { x } = dims
                    const scrollBuffer = width - containerWidth
                    containerRef.current.scrollLeft = (scrollBuffer / 100) * x
                  }
                }
              }}
            />
          </div>
        )}
        <Pagination
          pages={pages}
          activePage={activePage}
          onChange={(index: number) => {
            setActivePage(index)
          }}
        />
        <span className={styles.rowsLabelWrapper}>
          <SelectBoxSimple
            name="rows-per-page"
            value={rowsPerPage}
            onChange={(rows: number) => {
              setRowsPerPage(rows)
              setActivePage(1)
            }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="150">150</option>
          </SelectBoxSimple>
        </span>
      </ButtonRow>
      {showModal && <BulkImport setShowModal={setShowModal} />}
      {showQrModal && (
        <CreateQrModal
          section="view"
          setShowModal={setShowQrModal}
          codes={codes}
          selectedCodes={selectedCodes}
        />
      )}
      {showDeleteModal && (
        <BulkDeleteModal
          selectedCodesCanDelete={selectedCodesCanDelete}
          isAdmin={isAdminUser}
          setShowModal={setShowDeleteModal}
          selectedCodes={selectedCodes}
          onDelete={(codesNotDeleted) => {
            setSelectedCodes(codesNotDeleted)
          }}
        />
      )}
    </div>
  )
}
